
export default function USPSection() {
  const usps = [
    {
      title: 'Onafhankelijkheid en Eigenaarschap',
      description: 'Uw eigen platform, volledig onafhankelijk van vervoerders. Tools voor regie, beheer, gebruik én communicatie.',
      icon: 'ri-shield-check-line',
      color: 'from-blue-500 to-blue-600',
      features: [
        'Volledige controle over uw vervoersprocessen',
        'Onafhankelijk van externe vervoerders',
        'Transparante en eerlijke ritberekening',
        'Voorkomen van conflicten en misverstanden'
      ]
    },
    {
      title: 'Connectiviteit',
      description: 'Hét centrale punt voor informatie. Alle betrokken actoren zijn verbonden: Reiziger, Planner, Vervoerder, Controller, Directie.',
      icon: 'ri-links-line',
      color: 'from-teal-500 to-teal-600',
      features: [
        'Toegankelijk via elk apparaat',
        'Webapplicatie voor computer, tablet en smartphone',
        'Altijd beschikbaar met internetverbinding',
        'Geïntegreerde communicatie tussen alle partijen'
      ]
    },
    {
      title: 'Realtime Inzicht',
      description: 'Plug & play applicatie voor realtime monitoring en afrekening. Directe ontsluiting van data voor statistische informatie en besluitvorming.',
      icon: 'ri-dashboard-3-line',
      color: 'from-green-500 to-green-600',
      features: [
        'Flexibel systeem voor alle ritsoorten',
        'Standaardritten, complexe routes en groepsvervoer',
        'Dagelijks woon-werkverkeer en speciale opdrachten',
        'Realtime monitoring en directe feedback'
      ]
    }
  ];

  return (
    <section id="usp" className="py-12 sm:py-16 lg:py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            De Kernwaarden van EasyTravel
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
            Drie fundamentele pijlers die uw vervoersprocessen transformeren
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {usps.map((usp, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all p-6 sm:p-8 border border-gray-200 group"
            >
              <div className="text-center mb-6">
                <div className={`w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br ${usp.color} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                  <i className={`${usp.icon} text-3xl sm:text-4xl text-white`}></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
                  {usp.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                  {usp.description}
                </p>
              </div>

              <div className="space-y-3">
                {usp.features.map((feature, fIndex) => (
                  <div key={fIndex} className="flex items-start space-x-3">
                    <div className="w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                    <span className="text-sm text-gray-700 leading-relaxed">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Additional Benefits */}
        <div className="mt-12 sm:mt-16 bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-6 sm:p-8 lg:p-12">
          <div className="text-center mb-6 sm:mb-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Waarom Kiezen voor EasyTravel?
            </h3>
            <p className="text-gray-300 text-base sm:text-lg">
              Met onze slimme technologie trek je een rode lijn in afspraken en voorkom je misverstanden
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center border border-white/20">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-teal-500 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-eye-line text-white text-lg sm:text-xl"></i>
              </div>
              <h4 className="text-white font-bold mb-2 text-sm sm:text-base">Transparantie</h4>
              <p className="text-gray-300 text-xs sm:text-sm">Eerlijke en transparante ritberekening tussen alle partijen</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center border border-white/20">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-500 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-settings-3-line text-white text-lg sm:text-xl"></i>
              </div>
              <h4 className="text-white font-bold mb-2 text-sm sm:text-base">Flexibiliteit</h4>
              <p className="text-gray-300 text-xs sm:text-sm">Geschikt voor verschillende ritsoorten en complexe routes</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center border border-white/20">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-smartphone-line text-white text-lg sm:text-xl"></i>
              </div>
              <h4 className="text-white font-bold mb-2 text-sm sm:text-base">Toegankelijkheid</h4>
              <p className="text-gray-300 text-xs sm:text-sm">Toegankelijk via elk apparaat, altijd en overal beschikbaar</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 text-center border border-white/20">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-500 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-time-line text-white text-lg sm:text-xl"></i>
              </div>
              <h4 className="text-white font-bold mb-2 text-sm sm:text-base">Efficiëntie</h4>
              <p className="text-gray-300 text-xs sm:text-sm">Bespaart tijd en zorgt voor duidelijkheid richting opdrachtgevers</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
